import { useMemo } from "react";
import { useQueryData } from "@src/hooks/useQueryData";
import type { StockObjExtends } from "@src/api/stock/stockObjs";
import {
    wsDepotCardsServices,
    type DepotCardMovementExtends,
    type WsDepotCard,
    type WsDepotCardExtends,
} from "../../../../../api/wsDepotCards";
import { DepotCardType } from "@src/types/business/depotCardType";
import { groupMovementsByYearAndMonth, type GroupedMovements } from "../helpers/groupMovements";

/** Связи карточки УП для экрана движений. */
export const WS_DEPOT_CARD_MOVEMENTS_CARD_INCLUDES = {
    include: ["Division", "DivisionSection", "Measure", "ProductionType", "Stockobj"] satisfies Array<
        keyof WsDepotCardExtends
    >,
    includeStockObj: ["KoTypeObj"] satisfies Array<keyof StockObjExtends>,
} as const;

/** Связи движений карточки УП для экрана движений. */
export const WS_DEPOT_CARD_MOVEMENTS_INCLUDES = {
    include: ["DocType"] satisfies Array<keyof DepotCardMovementExtends>,
} as const;

export interface WsDepotCardMovementsQueryData {
    card: WsDepotCard;
    movementGroups: GroupedMovements[];
}

export interface UseWsDepotCardMovementsQueryResult {
    card: WsDepotCard | undefined;
    movementGroups: GroupedMovements[];
    isFetching: boolean;
    refetch: () => Promise<unknown>;
}

/**
 * Server-state карточки цеховой номенклатуры и её движений.
 */
export function useWsDepotCardMovementsQuery(cardCode: number): UseWsDepotCardMovementsQueryResult {
    const query = useQueryData<WsDepotCardMovementsQueryData>({
        queryKey: ["workshop", "wsDepotCard", cardCode, "movements"],
        queryFn: async ({ signal }) => {
            const card = await wsDepotCardsServices.loadOne(
                {
                    flt: { Codes: [cardCode] },
                    ...WS_DEPOT_CARD_MOVEMENTS_CARD_INCLUDES,
                },
                { signal },
            );

            if (card == null) {
                throw new Error("Ошибка в получении данных о карточке");
            }

            const depotCardMovements = await wsDepotCardsServices.movements.load({
                flt: { DepotCardType: DepotCardType.Ws, DepotCardCode: cardCode },
                ...WS_DEPOT_CARD_MOVEMENTS_INCLUDES,
            });
            const movementGroups = Object.values(groupMovementsByYearAndMonth(depotCardMovements, "desc")).reverse();

            return { card, movementGroups };
        },
        enabled: cardCode > 0,
        meta: { errorSource: "workshop.wsDepotCard.movements" },
    });

    const movementGroups = useMemo(() => query.data?.movementGroups ?? [], [query.data?.movementGroups]);

    return {
        card: query.data?.card,
        movementGroups,
        isFetching: query.isFetching,
        refetch: query.refetch,
    };
}
